import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { theme, type as text } from "../../theme";
import { api, apiErrorMessage } from "../../api/client";
import { LoadingBlock } from "../../components/Spinner";
import {
  ASSET_KINDS, labelOf, SeverityPill, ModalOverlay, ModalHeader, input, secondaryBtn, dangerBtn,
  type Asset, type AssetLink, type ImpactedAsset, type ImpactResult, type IncidentImpact,
} from "./shared";

// The structured dependency graph on the asset register: the editor inside the
// asset form (rows saved through PUT /resilience/assets/:id/dependencies once the
// asset itself is saved), the "If this fails" view (GET /resilience/assets/:id/impact)
// and the compact line an incident shows from its computed impact.

// Mirror of backend/src/lib/assetGraph.ts DEPENDENCY_KINDS.
export const DEPENDENCY_KINDS: Array<{ id: string; label: string }> = [
  { id: "requires", label: "Requires" },
  { id: "hosted_on", label: "Hosted on" },
  { id: "data_from", label: "Takes data from" },
  { id: "delivered_by", label: "Delivered by" },
  { id: "located_at", label: "Located at" },
];

export interface DependencyRow { dependsOnAssetId: string; kind: string; note: string; /** "discovery" for the seeded delivered_by edge — shown, never edited here. */ source?: string }

/** The kind the target's type suggests, so a new row usually needs no second click. */
const suggestKind = (targetKind: string | undefined): string => {
  switch (targetKind) {
    case "server": case "cloud_service": case "system": case "network": return "hosted_on";
    case "data": return "data_from";
    case "supplier_service": return "delivered_by";
    case "site_infrastructure": return "located_at";
    default: return "requires";
  }
};

export const linksToRows = (links: AssetLink[]): DependencyRow[] => links.map((l) => ({ dependsOnAssetId: l.assetId, kind: l.linkKind, note: l.note ?? "", source: l.source }));

const isSeeded = (r: DependencyRow) => r.source === "discovery";

export async function loadDependencies(assetId: string): Promise<DependencyRow[]> {
  const d = await api.get<{ asset: { dependsOn: AssetLink[] } }>(`/resilience/assets/${assetId}`);
  return linksToRows(d.asset.dependsOn);
}

export async function saveDependencies(assetId: string, rows: DependencyRow[]): Promise<void> {
  await api.put(`/resilience/assets/${assetId}/dependencies`, {
    dependencies: rows.filter((r) => r.dependsOnAssetId && !isSeeded(r)).map((r) => ({ dependsOnAssetId: r.dependsOnAssetId, kind: r.kind, note: r.note.trim() || null })),
  });
}

const assetOption = (a: Asset) => `${a.name} (${labelOf(ASSET_KINDS, a.kind)})${a.status === "retired" ? " — retired" : ""}`;

export function DependencyEditor({ selfId, assets, rows, onChange, loading }: { selfId: string | null; assets: Asset[]; rows: DependencyRow[]; onChange: (rows: DependencyRow[]) => void; loading?: boolean }) {
  const candidates = assets.filter((a) => a.id !== selfId).sort((a, b) => a.name.localeCompare(b.name));
  const byId = new Map(assets.map((a) => [a.id, a]));
  const set = (i: number, patch: Partial<DependencyRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const pick = (i: number, id: string) => set(i, { dependsOnAssetId: id, kind: rows[i].dependsOnAssetId ? rows[i].kind : suggestKind(byId.get(id)?.kind) });
  const taken = new Set(rows.map((r) => r.dependsOnAssetId).filter(Boolean));
  if (loading) return <div style={text.hint}>Loading dependencies…</div>;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {rows.map((r, i) => isSeeded(r) ? (
        <div key={i} style={{ ...text.hint, display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1.4fr) auto", gap: 6, alignItems: "center" }} title="Drawn from the asset's supplier link — clear the supplier on the asset to remove it">
          <span style={{ color: theme.color.text }}>{byId.get(r.dependsOnAssetId) ? assetOption(byId.get(r.dependsOnAssetId)!) : r.dependsOnAssetId}</span>
          <span>{labelOf(DEPENDENCY_KINDS, r.kind)}</span>
          <span style={{ color: theme.color.faint }}>From the supplier link</span>
          <span />
        </div>
      ) : (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1.4fr) auto", gap: 6, alignItems: "center" }}>
          <select value={r.dependsOnAssetId} onChange={(e) => pick(i, e.target.value)} style={input} aria-label="Depends on">
            <option value="">Choose an asset…</option>
            {candidates.map((a) => <option key={a.id} value={a.id} disabled={taken.has(a.id) && a.id !== r.dependsOnAssetId}>{assetOption(a)}</option>)}
          </select>
          <select value={r.kind} onChange={(e) => set(i, { kind: e.target.value })} style={input} aria-label="Link kind">{DEPENDENCY_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select>
          <input value={r.note} onChange={(e) => set(i, { note: e.target.value })} style={input} placeholder="Note (optional)" maxLength={500} aria-label="Note" />
          <button type="button" style={dangerBtn} onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove dependency">Remove</button>
        </div>
      ))}
      {rows.length === 0 && <div style={text.hint}>{candidates.length ? "Nothing recorded yet. Add the systems, data sets, supplier services and sites this asset cannot operate without." : "Add other assets to the register first, then link them here."}</div>}
      <div><button type="button" style={secondaryBtn} disabled={candidates.length === 0} onClick={() => onChange([...rows, { dependsOnAssetId: "", kind: "requires", note: "" }])}><Plus size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Add dependency</button></div>
    </div>
  );
}

function ImpactList({ items, emptyText }: { items: ImpactedAsset[]; emptyText: string }) {
  if (items.length === 0) return <div style={text.hint}>{emptyText}</div>;
  return (
    <ul style={{ ...text.body, margin: 0, paddingLeft: 18, display: "grid", gap: 6 }}>
      {items.map((a) => (
        <li key={a.id}>
          <span style={{ fontWeight: 600 }}>{a.name}</span> <SeverityPill value={a.criticality} />{a.isEssentialFunction && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "var(--c-accentText)" }}>ESSENTIAL</span>}
          <div style={text.meta}>{a.depth === 0 ? "Directly affected" : a.pathText}</div>
        </li>
      ))}
    </ul>
  );
}

type ImpactView = { asset: { id: string; name: string; kind: string; criticality: string; isEssentialFunction: boolean }; impact: ImpactResult; restsOn: Array<{ id: string; name: string; kind: string; criticality: string; status: string; supplierName: string | null; linkKind: string; note: string | null; depth: number; viaId: string; viaName: string }> };

/** "If this fails": everything that depends on the asset, transitively, and what it rests on. */
export function ImpactModal({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const [view, setView] = useState<ImpactView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.get<ImpactView>(`/resilience/assets/${asset.id}/impact`).then((v) => { if (!cancelled) setView(v); }).catch((e) => { if (!cancelled) setError(apiErrorMessage(e, "Could not load the impact")); });
    return () => { cancelled = true; };
  }, [asset.id]);
  const total = view ? view.impact.businessServices.length + view.impact.other.length : 0;
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={<span>If {asset.name} fails <SeverityPill value={asset.criticality} /></span>} onClose={onClose} />
      <div style={{ padding: 20, display: "grid", gap: 16 }}>
        {error && <div style={{ ...text.body, color: theme.color.dangerText }}>{error}</div>}
        {!view && !error && <LoadingBlock />}
        {view && (
          <>
            <div style={{ ...text.hint, lineHeight: 1.5 }}>
              {total === 0
                ? "Nothing in the register depends on this asset. Open the business services and systems that need it and add it as a dependency, so incidents and supplier changes show their reach."
                : `${total} asset${total === 1 ? "" : "s"} depend${total === 1 ? "s" : ""} on it, ${view.impact.businessServices.length} of them business services or essential functions.${view.impact.truncated ? ` The walk stopped at ${view.impact.maxDepth} links; deeper dependents are not listed.` : ""}`}
            </div>
            <section>
              <h3 style={{ ...text.sectionTitle, margin: "0 0 8px" }}>Business services affected</h3>
              <ImpactList items={view.impact.businessServices} emptyText="No business service or essential function reaches this asset." />
            </section>
            <section>
              <h3 style={{ ...text.sectionTitle, margin: "0 0 8px" }}>Other assets affected</h3>
              <ImpactList items={view.impact.other} emptyText="None." />
            </section>
            <section>
              <h3 style={{ ...text.sectionTitle, margin: "0 0 8px" }}>What it rests on</h3>
              {view.restsOn.length === 0 ? <div style={text.hint}>No dependency recorded for this asset.</div> : (
                <ul style={{ ...text.body, margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
                  {view.restsOn.map((d) => (
                    <li key={d.id}>
                      <span style={{ fontWeight: 600 }}>{d.name}</span>{d.status === "retired" && <span style={{ marginLeft: 6, fontSize: 10, color: theme.color.warningText }}>RETIRED</span>}
                      <span style={text.meta}> — {labelOf(DEPENDENCY_KINDS, d.linkKind).toLowerCase()}{d.viaId !== asset.id ? ` via ${d.viaName}` : ""}{d.supplierName ? ` · ${d.supplierName}` : ""}{d.note ? ` · ${d.note}` : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
        <div><button type="button" style={secondaryBtn} onClick={onClose}>Close</button></div>
      </div>
    </ModalOverlay>
  );
}

/** One compact line for the incident detail: the business services the incident's assets take down. */
export function IncidentImpactLine({ impact }: { impact?: IncidentImpact }) {
  const services = impact?.businessServices ?? [];
  return (
    <div style={{ ...text.hint, marginTop: 8, lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600, color: theme.color.text }}>Business services affected: </span>
      {services.length === 0
        ? <span>none mapped{impact && impact.otherCount > 0 ? ` (${impact.otherCount} other asset${impact.otherCount === 1 ? "" : "s"} depend on the linked assets)` : ""} — link the assets involved and record their dependencies to see what the incident takes down</span>
        : services.map((s, i) => <span key={s.id} title={s.pathText || "Linked to the incident directly"}>{i > 0 ? ", " : ""}<span style={{ color: theme.color.text }}>{s.name}</span>{s.depth === 0 ? " (directly)" : ` (via ${s.path[s.path.length - 1]?.name ?? "linked asset"})`}</span>)}
      {services.length > 0 && impact && impact.otherCount > 0 && <span> · {impact.otherCount} other asset{impact.otherCount === 1 ? "" : "s"}</span>}
      {impact?.truncated && <span> · not every dependent was walked</span>}
    </div>
  );
}
